/**
 * Post-processing effects manager.
 *
 * Key design decision: we avoid the previous pattern of 4 different return
 * paths (null, SSAO+Bloom, SSAO-only, Bloom-only) that caused the entire
 * EffectComposer to be unmounted and remounted on every toggle. That
 * remount destroyed all GPU render targets and caused frames where bloom
 * was not applied and materials appeared white.
 *
 * New approach:
 * - The EffectComposer stays mounted whenever ANY effect is enabled.
 * - Bloom is always a child of the composer; when disabled, intensity=0
 *   makes it a visual no-op without remounting.
 * - SSAO is rendered conditionally since it requires enableNormalPass.
 *   We use a stable key and memoisation to minimise churn.
 * - When nothing is enabled, the composer is disabled via enabled={false}
 *   so R3F skips the extra render passes.
 */

import { EffectComposer, SSAO, Bloom } from '@react-three/postprocessing';
import type { SceneSettings } from '@/contexts/SceneSettingsContext';

/**
 * Inner component: both SSAO + Bloom as children.
 * Extracted so the EffectComposer always sees a stable children array type.
 */
function SSAOAndBloom({ sceneSettings }: { sceneSettings: SceneSettings }) {
  const bloomEnabled = sceneSettings.bloom.enabled;
  return (
    <>
      <SSAO
        intensity={sceneSettings.ssao.intensity}
        radius={sceneSettings.ssao.aoRadius}
        samples={sceneSettings.ssao.samples}
        luminanceInfluence={sceneSettings.ssao.luminanceInfluence}
      />
      <Bloom
        intensity={bloomEnabled ? sceneSettings.bloom.intensity : 0}
        luminanceThreshold={sceneSettings.bloom.luminanceThreshold}
        luminanceSmoothing={sceneSettings.bloom.luminanceSmoothing}
        mipmapBlur={sceneSettings.bloom.mipmapBlur}
      />
    </>
  );
}

/**
 * Inner component: Bloom only (no SSAO).
 */
function BloomOnly({ sceneSettings }: { sceneSettings: SceneSettings }) {
  const bloomEnabled = sceneSettings.bloom.enabled;
  return (
    <Bloom
      intensity={bloomEnabled ? sceneSettings.bloom.intensity : 0}
      luminanceThreshold={sceneSettings.bloom.luminanceThreshold}
      luminanceSmoothing={sceneSettings.bloom.luminanceSmoothing}
      mipmapBlur={sceneSettings.bloom.mipmapBlur}
    />
  );
}

export function PostProcessing({
  sceneSettings,
  multisampling = 8,
}: {
  sceneSettings: SceneSettings;
  /** MSAA sample count for the EffectComposer (default 8, lower = faster) */
  multisampling?: number;
}) {
  const ssaoEnabled = sceneSettings.ssao.enabled;
  const bloomEnabled = sceneSettings.bloom.enabled;
  const anyEnabled = ssaoEnabled || bloomEnabled;

  // The composer key only changes when the SSAO toggle changes (which requires
  // enableNormalPass to change). Bloom toggles do NOT change the key — the
  // composer stays mounted.
  const composerKey = ssaoEnabled ? 'ssao' : 'no-ssao';

  if (!anyEnabled) return null;

  return (
    <EffectComposer
      key={composerKey}
      enabled={anyEnabled}
      enableNormalPass={ssaoEnabled}
      multisampling={multisampling}
    >
      {ssaoEnabled ? (
        <SSAOAndBloom sceneSettings={sceneSettings} />
      ) : (
        <BloomOnly sceneSettings={sceneSettings} />
      )}
    </EffectComposer>
  );
}
